import React, { useState } from 'react';
import { Layout, Menu, Avatar, Dropdown, Space, Typography, theme } from 'antd';
import {
  DashboardOutlined,
  TeamOutlined,
  BankOutlined,
  DesktopOutlined,
  AuditOutlined,
  SettingOutlined,
  UserOutlined,
  LogoutOutlined,
} from '@ant-design/icons';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const adminMenuItems = [
  {
    key: '/dashboard',
    icon: <DashboardOutlined />,
    label: 'Dashboard',
  },
  {
    key: '/companies',
    icon: <BankOutlined />,
    label: 'Empresas',
  },
  {
    key: '/users',
    icon: <TeamOutlined />,
    label: 'Usuários',
  },
  {
    key: '/devices',
    icon: <DesktopOutlined />,
    label: 'Dispositivos',
  },
  {
    key: '/sessions',
    icon: <AuditOutlined />,
    label: 'Sessões',
  },
  {
    key: '/audit',
    icon: <AuditOutlined />,
    label: 'Auditoria',
  },
  {
    key: '/settings',
    icon: <SettingOutlined />,
    label: 'Configurações',
  },
];

const operatorMenuItems = [
  {
    key: '/dashboard',
    icon: <DashboardOutlined />,
    label: 'Dashboard',
  },
  {
    key: '/devices',
    icon: <DesktopOutlined />,
    label: 'Dispositivos',
  },
  {
    key: '/sessions',
    icon: <AuditOutlined />,
    label: 'Sessões',
  },
];

const AppLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { token } = theme.useToken();

  const isAdmin = user?.profile === 'SUPER_ADMIN' || user?.profile === 'ADMIN';
  const menuItems = isAdmin ? adminMenuItems : operatorMenuItems;

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Meu Perfil',
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Sair',
    },
  ];

  const handleUserMenuClick = ({ key }: { key: string }) => {
    if (key === 'logout') {
      logout();
      navigate('/login');
    } else if (key === 'profile') {
      navigate('/profile');
    }
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        theme="dark"
      >
        <div style={{ 
          height: 64, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          padding: '0 16px'
        }}>
          <img 
            src="data:image/png;base64,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" 
            alt="Layout Sistemas" 
            style={{ height: 40 }}
          />
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={({ key }) => navigate(key)}
        />
      </Sider>
      
      <Layout>
        <Header style={{ 
          background: token.colorBgContainer, 
          padding: '0 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
        }}>
          <Text strong style={{ fontSize: 16 }}>
            Layout Sistemas - Painel Administrativo
          </Text>
          
          <Dropdown
            menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
            placement="bottomRight"
          >
            <Space style={{ cursor: 'pointer' }}>
              <Avatar icon={<UserOutlined />} />
              <Text>{user?.full_name || user?.email}</Text>
            </Space>
          </Dropdown>
        </Header>
        
        <Content style={{ margin: 24, padding: 24, background: token.colorBgContainer, borderRadius: 8 }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default AppLayout;